import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Layout, Steps, Typography } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dropzone,
  DropzoneFile,
  DropzoneFileStatus,
} from "../components/Dropzone";
import { UNKNOWN_ERROR_MESSAGE } from "../constants";
import { persistLinkState } from "../hooks/useLinkTracker";
import { captureException } from "../sentry";
import Store from "../stateStore";
import { extractLinkParamsFromState, getBackendUrl, STORES } from "../utils";

const { Step } = Steps;
const { Paragraph, Text, Title } = Typography;

// Native RDP clipboard transfer only supports files smaller than 2 GiB.
const MAX_BACKUP_SIZE = 2 * 1024 * 1024 * 1024 - 1;
const SETUP_STORAGE_KEY = "rutterSage50HostedBackupSetup";
const DATA_ACCESS_PASSWORD = /^(?=.*[A-Za-z])(?=.*\d).{7}$/;

type Props = {
  clientSecret: string;
  onBack: () => any;
  onCancel: () => any;
  onComplete: (public_token: string) => any;
};

type UploadStatus = "idle" | "uploading" | "processing" | "failure";

type UploadSession = {
  is_successful: boolean;
  reason?: string;
  error_message?: string;
  item_id: string;
  upload_token: string;
};

type StoredUpload = {
  orgId: string;
  itemId: string;
};

export function isHostedSage50Platform(platform: string | null) {
  return platform?.toLowerCase() === "sage50_hosted";
}

function apiError(error: any) {
  return (
    error?.response?.data?.reason ??
    error?.response?.data?.error_message ??
    error?.message ??
    UNKNOWN_ERROR_MESSAGE
  );
}

export default function Sage50Hosted(props: Props) {
  const store = Store.useStore();
  const orgId = store.get("orgId");
  const autoPlatform = store.get("autoPlatform");
  const [username, setUsername] = useState("Peachtree");
  const [password, setPassword] = useState("");
  const [files, setFiles] = useState<DropzoneFile[]>([]);
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState("");
  const [activeUpload, setActiveUpload] = useState<StoredUpload | null>(() => {
    try {
      const value = window.sessionStorage.getItem(SETUP_STORAGE_KEY);
      if (!value) return null;
      const parsed = JSON.parse(value) as StoredUpload;
      return parsed.orgId === orgId ? parsed : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (activeUpload) setStatus("processing");
  }, [activeUpload]);

  const selectedFile = files[0];
  const passwordIsValid = DATA_ACCESS_PASSWORD.test(password);
  const canUpload = !!selectedFile && !!username.trim() && passwordIsValid;

  const updateSelectedFile = useCallback((changes: Partial<DropzoneFile>) => {
    setFiles((current) =>
      current.map((file, index) =>
        index === 0 ? { ...file, ...changes } : file,
      ),
    );
  }, []);

  const handleFiles = useCallback((nextFiles: DropzoneFile[]) => {
    const valid = nextFiles.filter((file) =>
      file.binary.name.toLowerCase().endsWith(".ptb"),
    );
    setError(
      valid.length === nextFiles.length
        ? ""
        : "Choose a Sage 50 backup file with a .ptb extension.",
    );
    setFiles(valid.slice(-1));
  }, []);

  const verifyUpload = useCallback(async () => {
    if (!activeUpload) return;
    try {
      const { data } = await axios.post(
        getBackendUrl() + "/sage-50/link-verify",
        {
          ...extractLinkParamsFromState(store),
          link_update_id: activeUpload.itemId,
        },
      );
      if (data.is_successful && data.public_token) {
        window.sessionStorage.removeItem(SETUP_STORAGE_KEY);
        props.onComplete(data.public_token);
      } else if (data.status === "failed") {
        setStatus("failure");
        setError(data.reason ?? data.error_message ?? UNKNOWN_ERROR_MESSAGE);
      }
    } catch (e) {
      captureException(e);
      setStatus("failure");
      setError(apiError(e));
    }
  }, [activeUpload, props, store]);

  useEffect(() => {
    if (status !== "processing" || !activeUpload) return;
    verifyUpload();
    const interval = window.setInterval(verifyUpload, 5000);
    return () => window.clearInterval(interval);
  }, [activeUpload, status, verifyUpload]);

  const uploadBackup = async () => {
    if (!selectedFile || !canUpload) return;
    setStatus("uploading");
    setError("");
    updateSelectedFile({ status: DropzoneFileStatus.Uploading, progress: 0 });
    persistLinkState(STORES.SAGE_50, store.getState());

    try {
      const { data } = await axios.post<UploadSession>(
        getBackendUrl() + "/sage-50/setup-session",
        {
          ...extractLinkParamsFromState(store),
          link_update_id: store.get("itemId") || undefined,
          mode: "hosted_backup",
        },
      );
      if (!data.is_successful || !data.upload_token) {
        throw new Error(
          data.reason ?? data.error_message ?? UNKNOWN_ERROR_MESSAGE,
        );
      }

      store.set("itemId")(data.item_id);
      const upload: StoredUpload = {
        orgId,
        itemId: data.item_id,
      };

      const formData = new FormData();
      formData.append("file", selectedFile.binary);
      await axios.post(
        getBackendUrl() +
          `/versioned/connections/${data.item_id}/customer_spend`,
        formData,
        {
          headers: {
            "X-Rutter-Version": "2023-02-07",
            "X-Sage50-Setup-Token": data.upload_token,
            "X-Sage50-Data-Access-Username": username.trim(),
            "X-Sage50-Data-Access-Password": password,
          },
          onUploadProgress: (progress: ProgressEvent) => {
            const percent = progress.total
              ? Math.round((progress.loaded / progress.total) * 100)
              : 0;
            updateSelectedFile({ progress: percent });
          },
        },
      );
      updateSelectedFile({ status: DropzoneFileStatus.Success, progress: 100 });

      window.sessionStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(upload));
      setActiveUpload(upload);
      setPassword("");
      setStatus("processing");
    } catch (e) {
      captureException(e);
      updateSelectedFile({ status: DropzoneFileStatus.Failure });
      setStatus("failure");
      setError(apiError(e));
    }
  };

  const passwordHelp = useMemo(() => {
    if (!password || passwordIsValid) return undefined;
    return "The Sage data-access password must be exactly seven characters and include a letter and a number.";
  }, [password, passwordIsValid]);

  return (
    <Layout className="bg-white w-full">
      <Layout.Content className="overflow-y-auto px-6 sm:px-16">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[800px] py-8">
            {!autoPlatform && (
              <Button onClick={props.onBack} icon={<ArrowLeftOutlined />}>
                Back
              </Button>
            )}

            <div className="text-center mb-8">
              <img
                className="h-20 inline-block"
                src="https://superspeed-ph.com/wp-content/uploads/2018/09/sage50-new.png"
                alt="Sage 50"
              />
              <Title level={3} className="mt-4">
                Connect hosted Sage 50 with a backup
              </Title>
              <Text type="secondary">
                Create a Sage backup, copy it from the hosted desktop, and
                upload it securely to Rutter.
              </Text>
            </div>

            <Alert
              className="mb-6"
              type="info"
              showIcon
              message="This creates a snapshot connection"
              description="Rutter imports the data contained in this backup. To refresh the connection later, upload a newer backup."
            />

            <Steps
              direction="vertical"
              current={status === "processing" ? 3 : 2}
            >
              <Step
                title="Create the Sage 50 backup"
                description={
                  <div className="space-y-3 pb-4">
                    <Paragraph>
                      1. Open the company in the main Sage 50 application as a
                      Sage administrator.
                    </Paragraph>
                    <Paragraph>
                      2. Go to{" "}
                      <Text strong>
                        Maintain → Users → Set Up User Security
                      </Text>
                      , open <Text strong>Crystal Reports/Data Access</Text>,
                      and under <Text strong>Access from Outside Sage 50</Text>{" "}
                      select
                      <Text strong> With the following login information</Text>.
                    </Paragraph>
                    <Paragraph>
                      3. Click <Text strong>Change</Text>, note the displayed
                      user ID, and create a seven-character password containing
                      at least one letter and one number.
                    </Paragraph>
                    <Paragraph>
                      4. Create a new <Text code>.ptb</Text> backup and save it
                      in
                      <Text code> G:\Backups</Text>.
                    </Paragraph>
                  </div>
                }
              />

              <Step
                title="Copy the backup to this computer"
                description={
                  <div className="space-y-3 pb-4">
                    <Alert
                      type="warning"
                      showIcon
                      message="Backup files must be smaller than 2 GB"
                      description="At 2 GB or larger, Windows App copy and paste can fail without showing an error, and Rutter Link will not accept the file. Ask your Rutter contact for an alternative transfer method before continuing."
                    />
                    <Paragraph>
                      1. Open <Text strong>Sage 50 US Backup</Text> in Windows
                      App and browse to <Text code>G:\Backups</Text>.
                    </Paragraph>
                    <Paragraph>
                      2. Select the newest <Text code>.ptb</Text> file and press
                      <Text strong> Ctrl+C</Text> inside the remote Windows
                      window.
                    </Paragraph>
                    <Paragraph>
                      3. Switch out of Windows App and open a local folder such
                      as Downloads on this computer.
                    </Paragraph>
                    <Paragraph>
                      4. Paste the file with <Text strong>Ctrl+V</Text> on
                      Windows or <Text strong>Command+V</Text> on Mac, then wait
                      for the copy to finish.
                    </Paragraph>
                    <Alert
                      type="warning"
                      showIcon
                      message="If the file does not paste"
                      description="Your Sage hosting administrator may have disabled clipboard file transfer. Use a redirected local drive or folder in Windows App, or ask the hosting administrator to enable remote-to-local file transfer."
                    />
                  </div>
                }
              />

              <Step
                title="Upload the local backup to Rutter"
                description={
                  <div className="space-y-4 pb-4">
                    <Paragraph>
                      Return to Rutter Link in your local browser and select the
                      <Text code> .ptb</Text> file you just copied to this
                      computer.
                    </Paragraph>
                    <Form layout="vertical">
                      <Form.Item label="Data-access username" required>
                        <Input
                          value={username}
                          onChange={(event) => setUsername(event.target.value)}
                          disabled={
                            status === "uploading" || status === "processing"
                          }
                          autoComplete="username"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Data-access password"
                        required
                        validateStatus={passwordHelp ? "error" : undefined}
                        help={passwordHelp}
                      >
                        <Input.Password
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          disabled={
                            status === "uploading" || status === "processing"
                          }
                          autoComplete="current-password"
                          placeholder="Exactly seven characters"
                        />
                      </Form.Item>
                    </Form>

                    <Dropzone
                      files={files}
                      onChange={handleFiles}
                      maxFiles={1}
                      maxSizePerFile={MAX_BACKUP_SIZE}
                      allowedExtensions={["ptb"]}
                      isUploading={status === "uploading"}
                      disabled={
                        status === "uploading" || status === "processing"
                      }
                      readonly={
                        status === "uploading" || status === "processing"
                      }
                    />

                    {error && <Alert type="error" showIcon message={error} />}

                    <div className="flex justify-between items-center">
                      <Button
                        onClick={props.onCancel}
                        disabled={status === "uploading"}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={status === "uploading"}
                        disabled={!canUpload || status === "processing"}
                        onClick={uploadBackup}
                      >
                        Upload Sage backup
                      </Button>
                    </div>
                  </div>
                }
              />

              <Step
                title="Rutter imports the company data"
                description={
                  status === "processing" ? (
                    <Paragraph>
                      The backup uploaded successfully. Keep this window open
                      while Rutter validates and imports it; this page checks
                      automatically every five seconds.
                    </Paragraph>
                  ) : (
                    <Text type="secondary">Upload a backup to begin.</Text>
                  )
                }
              />
            </Steps>
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
}
